/**
 * Sincroniza CTAs de cotización vs checkout en cart drawer y página /cart.
 */
function readRequiresQuoteFromHtml(html) {
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const sourceDrawer = doc.querySelector('cart-drawer');
  const sourceDrawerFooter = doc.querySelector('.cart-drawer__footer');
  const sourceMainFooter = doc.getElementById('main-cart-footer');

  let requiresQuote = null;

  if (sourceDrawer?.dataset?.cartRequiresQuote !== undefined) {
    requiresQuote = sourceDrawer.dataset.cartRequiresQuote === 'true';
  }

  if (sourceDrawerFooter?.dataset?.cartRequiresQuote !== undefined) {
    const footerRequiresQuote = sourceDrawerFooter.dataset.cartRequiresQuote === 'true';
    requiresQuote = requiresQuote === null ? footerRequiresQuote : requiresQuote || footerRequiresQuote;
  }

  if (sourceMainFooter?.dataset?.cartRequiresQuote !== undefined) {
    return sourceMainFooter.dataset.cartRequiresQuote === 'true';
  }

  return requiresQuote;
}

function readRequiresQuoteFromDrawerDom(cartDrawer) {
  const drawer = cartDrawer || document.querySelector('cart-drawer');
  if (!drawer || drawer.classList.contains('is-empty')) return null;

  const cartItems = drawer.querySelector('cart-drawer-items');
  if (cartItems?.classList.contains('is-empty')) return null;

  const quoteLines = drawer.querySelectorAll('[data-line-requires-quote="true"]');
  if (quoteLines.length > 0) return true;

  const madeToOrderLines = drawer.querySelectorAll(
    '.cart-item__made-to-order, .variant-item__made-to-order-badge'
  );
  if (madeToOrderLines.length > 0) return true;

  const cartRows = drawer.querySelectorAll('.cart-item');
  if (cartRows.length === 0) return null;

  return false;
}

function quoteActionsHasVisibleControl(quoteActions) {
  if (!quoteActions) return false;

  const controls = quoteActions.querySelectorAll(
    'button:not([data-quote-fallback]), a[href], [role="button"], input[type="submit"]'
  );

  for (const control of controls) {
    if (control.closest('.cart-drawer__quote-fallback, .cart__quote-fallback')) continue;
    if (control.hasAttribute('hidden') || control.classList.contains('hidden')) continue;
    if (control.offsetParent !== null || control.getClientRects().length > 0) return true;
  }

  return false;
}

function syncQuoteFallback(quoteActions) {
  const fallback = quoteActions?.querySelector('[data-quote-fallback]');
  if (!fallback) return;

  // El botón de cotizar debe estar SIEMPRE disponible (con o sin stock).
  // Si la app ya pinta su propio control, ocultamos el fallback; si no, lo mostramos.
  const showFallback = !quoteActionsHasVisibleControl(quoteActions);
  fallback.classList.toggle('hidden', !showFallback);
  fallback.toggleAttribute('hidden', !showFallback);
}

function resolveRequiresQuote({ requiresQuote, cartSectionHtml, regionRoot, cartDrawer }) {
  const parsedQuote = readRequiresQuoteFromHtml(cartSectionHtml);
  const domQuote = cartDrawer ? readRequiresQuoteFromDrawerDom(cartDrawer) : null;
  let regionRequiresQuote = requiresQuote;

  if (domQuote === true || parsedQuote === true) {
    regionRequiresQuote = true;
  } else if (domQuote === false && parsedQuote === false) {
    regionRequiresQuote = false;
  } else if (domQuote !== null) {
    regionRequiresQuote = domQuote;
  } else if (parsedQuote !== null) {
    regionRequiresQuote = parsedQuote;
  } else if (regionRoot?.dataset?.cartRequiresQuote !== undefined) {
    regionRequiresQuote = regionRoot.dataset.cartRequiresQuote === 'true';
  }

  return regionRequiresQuote;
}

function syncQuoteRegion(regionRoot, requiresQuote, cartSectionHtml, cartDrawer) {
  if (!regionRoot) return requiresQuote;

  const regionRequiresQuote = resolveRequiresQuote({
    requiresQuote,
    cartSectionHtml,
    regionRoot,
    cartDrawer,
  });

  regionRoot.dataset.cartRequiresQuote = regionRequiresQuote ? 'true' : 'false';

  const quoteActions = regionRoot.querySelector('[data-quote-actions]');
  const checkoutCta = regionRoot.querySelector('[data-checkout-cta]');

  if (quoteActions) {
    quoteActions.classList.remove('hidden');
    quoteActions.removeAttribute('hidden');
    syncQuoteFallback(quoteActions);
  }

  if (checkoutCta) {
    checkoutCta.classList.toggle('hidden', regionRequiresQuote);
    checkoutCta.toggleAttribute('hidden', regionRequiresQuote);
  }

  return regionRequiresQuote;
}

function syncCartQuoteState(cartSectionHtml) {
  let requiresQuote = false;

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const drawerFooter = cartDrawer.querySelector('.cart-drawer__footer');
    const drawerQuote = drawerFooter
      ? syncQuoteRegion(
          drawerFooter,
          cartDrawer.dataset.cartRequiresQuote === 'true',
          cartSectionHtml,
          cartDrawer
        )
      : resolveRequiresQuote({
          requiresQuote: cartDrawer.dataset.cartRequiresQuote === 'true',
          cartSectionHtml,
          regionRoot: cartDrawer,
          cartDrawer,
        });

    cartDrawer.dataset.cartRequiresQuote = drawerQuote ? 'true' : 'false';
    requiresQuote = drawerQuote;

    cartDrawer.dispatchEvent(
      new CustomEvent('cart-quote-state', {
        bubbles: true,
        detail: { requiresQuote: drawerQuote },
      })
    );
  }

  const mainFooter = document.getElementById('main-cart-footer');
  if (mainFooter) {
    const pageQuote = syncQuoteRegion(
      mainFooter.querySelector('[data-cart-footer-blocks]') || mainFooter,
      mainFooter.dataset.cartRequiresQuote === 'true',
      cartSectionHtml,
      null
    );
    mainFooter.dataset.cartRequiresQuote = pageQuote ? 'true' : 'false';
    requiresQuote = pageQuote;
  }

  document.dispatchEvent(
    new CustomEvent('cart-update', {
      detail: { source: 'cart-quote-sync', requiresQuote },
    })
  );

  document.dispatchEvent(
    new CustomEvent('cart-quote-state', {
      bubbles: true,
      detail: { requiresQuote },
    })
  );
}

function scheduleCartQuoteSync(cartSectionHtml) {
  const run = () => syncCartQuoteState(cartSectionHtml);
  run();
  requestAnimationFrame(run);
  setTimeout(run, 100);
  setTimeout(run, 500);
}

function bindCartQuoteFallback() {
  document.addEventListener('click', (event) => {
    const fallback = event.target.closest('[data-quote-fallback]');
    if (!fallback) return;

    event.preventDefault();

    const quoteActions = fallback.closest('[data-quote-actions]');
    const appControl = quoteActions?.querySelector(
      'button:not([data-quote-fallback]), a[href], [role="button"]'
    );

    if (appControl) {
      appControl.click();
      return;
    }

    if (fallback.dataset.quoteUrl) {
      window.location.href = fallback.dataset.quoteUrl;
    }
  });
}

function scheduleCartDrawerQuoteSync(cartSectionHtml) {
  scheduleCartQuoteSync(cartSectionHtml);
}

document.addEventListener('DOMContentLoaded', () => {
  bindCartQuoteFallback();
  scheduleCartQuoteSync();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        setTimeout(() => scheduleCartQuoteSync(), 0);
      } else {
        scheduleCartQuoteSync();
      }
    });
  }
});
